import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { setToken, setUser } from "../lib/auth";
import { toast } from "sonner";
import { Shield, Loader2, MonitorDot, Smartphone, ArrowLeft } from "lucide-react";

const AdminLogin = () => {
  const nav = useNavigate();
  const location = useLocation();
  const [smsStep, setSmsStep] = useState("phone"); // 'phone' | 'code'
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // If user already has a valid session (cookie), bounce to /admin
  useEffect(() => {
    if (location.hash?.includes("session_id=")) return;
    let alive = true;
    api.get("/admin/me").then(() => { if (alive) nav("/admin"); }).catch(() => {});
    return () => { alive = false; };
  }, [location.hash, nav]);

  // Countdown for OTP expiry
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const requestSms = async (e) => {
    e?.preventDefault?.();
    if (!phone.trim()) {
      toast.error("Veuillez saisir un numéro de téléphone");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/admin/auth/sms/request", { phone: phone.trim() });
      const ttl = res?.data?.expires_in || 300;
      setSecondsLeft(ttl);
      setSmsStep("code");
      toast.success("Code envoyé par SMS");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Envoi du SMS impossible";
      toast.error(typeof msg === "string" ? msg : "Envoi du SMS impossible");
    } finally {
      setSending(false);
    }
  };

  const verifySms = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(code)) {
      toast.error("Le code doit comporter 6 chiffres");
      return;
    }
    setVerifying(true);
    try {
      const res = await api.post("/admin/auth/sms/verify", { phone: phone.trim(), code });
      // Store session_token in localStorage as a Bearer fallback (in case 3rd-party / cross-domain cookies are blocked).
      if (res?.data?.session_token) setToken(res.data.session_token);
      setUser({ email: res?.data?.email || "", role: "admin", method: "sms" });
      toast.success("Connexion réussie");
      nav("/admin");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Code incorrect";
      toast.error(typeof msg === "string" ? msg : "Code incorrect");
    } finally {
      setVerifying(false);
    }
  };

  const resetSms = () => {
    setSmsStep("phone");
    setCode("");
    setSecondsLeft(0);
  };

  const fmtSeconds = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-5 py-12 font-preserve">
      <div className="w-full max-w-md" data-testid="admin-login-page">
        <Link to="/" className="inline-flex items-center gap-2 group" data-testid="admin-brand">
          <span className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shadow-md transition group-hover:shadow-lg">
            <MonitorDot className="w-5 h-5 text-white" strokeWidth={2.25} />
          </span>
          <span className="font-bold text-2xl tracking-tight">
            Le Guide des Entrepreneurs
          </span>
        </Link>
        <div className="flex items-center gap-2 mt-12 text-xs font-bold uppercase tracking-widest text-orange-400">
          <Shield className="w-3.5 h-3.5" /> Espace admin
        </div>
        <h1 className="font-heading font-black text-4xl tracking-tighter mt-3 mb-2">CMS Le Guide des Entrepreneurs</h1>
        <p className="text-white/60 mb-8 text-sm">
          Accès réservé aux administrateurs autorisés. Connexion sécurisée par code SMS à usage unique.
        </p>

        {smsStep === "phone" && (
          <form onSubmit={requestSms} className="space-y-4" data-testid="admin-sms-phone-form">
            <div>
              <Label className="text-white text-sm font-semibold mb-1.5 block">Numéro de téléphone admin</Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+33 6 XX XX XX XX"
                autoComplete="tel"
                className="rounded-none bg-white text-black border-white"
                data-testid="admin-sms-phone-input"
              />
              <p className="text-xs text-white/40 mt-1.5">
                Vous recevrez un code à 6 chiffres par SMS, valide 5 minutes.
              </p>
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="w-full rounded-none bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-semibold flex items-center justify-center gap-3"
              data-testid="admin-sms-request-btn"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Smartphone className="w-5 h-5" />}
              Recevoir le code par SMS
            </Button>
          </form>
        )}

        {smsStep === "code" && (
          <form onSubmit={verifySms} className="space-y-4" data-testid="admin-sms-code-form">
            <button
              type="button"
              onClick={resetSms}
              className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"
              data-testid="admin-sms-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Changer de numéro
            </button>
            <div>
              <Label className="text-white text-sm font-semibold mb-1.5 block">Code reçu par SMS</Label>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="• • • • • •"
                autoComplete="one-time-code"
                className="rounded-none bg-white text-black border-white text-center text-2xl tracking-[0.5em] font-mono"
                data-testid="admin-sms-code-input"
              />
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-xs text-white/40">SMS envoyé au {phone}</p>
                <p className="text-xs text-white/60 font-mono" data-testid="admin-sms-timer">
                  {secondsLeft > 0 ? `expire dans ${fmtSeconds(secondsLeft)}` : "code expiré"}
                </p>
              </div>
            </div>
            <Button
              type="submit"
              disabled={verifying || code.length !== 6}
              className="w-full rounded-none bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base font-semibold flex items-center justify-center gap-3"
              data-testid="admin-sms-verify-btn"
            >
              {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <Shield className="w-5 h-5" />}
              Vérifier et se connecter
            </Button>
            <button
              type="button"
              onClick={requestSms}
              disabled={sending || secondsLeft > 240}
              className="w-full text-xs text-white/60 hover:text-white disabled:opacity-40 underline-offset-2 hover:underline pt-2"
              data-testid="admin-sms-resend"
            >
              {secondsLeft > 240 ? `Renvoyer un code (disponible dans ${secondsLeft - 240}s)` : "Renvoyer un code SMS"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
